import React from 'react'
import ReactDOM from 'react-dom'
import { ThemeProvider, MobileThemeProvider, MobileMode } from '@amzn/storm-ui'
import { AAC_FIT_ANCHOR_MOUNT, AAC_FIT_ANCHOR_UNMOUNT } from '@amzn/intro-widgets-api'
import { StyleSheetManager } from 'styled-components'
import rtlPlugin from 'stylis-plugin-rtl'
import { Element } from './Element'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { logger } from '../utils/logger'
import { KatalMetricsPublisher } from '../utils/metrics'
import { Success, Total } from '../common/constants'
import { WhatsNewCenter } from './WhatsNewCenter'
import { MetadataProvider } from '../contexts/MetadataContext'
import { filterWidgetsWithDismissal, getIsMobileView, getFeatureLabels, NoopPlugin } from '../utils/functions'
import { customizeTheme } from '../utils/palette'
import { FeedbackModal } from '../components/FeedbackModal'

interface InitParams {
  introWidgetMetadata: any[] | []
}
declare global {
  interface Window {
    __AAC_FIT_MANAGER_READY__: any
    __AAC_FIT_NEW_FEATURE_LOADED__: any
    __AAC_FIT_FEEDBACK_MODAL_READY__: any
    __AAC_FIT_CSRF_TOKEN__: string
  }
}

const metricsPublisherForManager = KatalMetricsPublisher.newChildActionPublisherForMethod('ManagerMount')

const mountComponent =
  ({ introWidgetMetadata, mobileView }) =>
  (event) => {
    const anchorId = event.detail.id
    const anchorEl = event.detail.anchor
    const mountLocation = event.detail.mountLocation
    metricsPublisherForManager.publishCounterMonitor(Total, 1)
    try {
      if (anchorEl) {
        logger.info('mounting react component', { anchorId, mobileView })
        ReactDOM.render(
          <ErrorBoundary anchorId={anchorId} mobileView={mobileView}>
            <ThemeProvider>
              <ThemeProvider theme={(theme) => customizeTheme(theme, mountLocation)}>
                <MobileThemeProvider mobileMode={mobileView ? MobileMode.Enable : MobileMode.Disable}>
                  <StyleSheetManager stylisPlugins={document.documentElement.dir === 'rtl' ? [rtlPlugin, NoopPlugin] : [NoopPlugin]}>
                    <MetadataProvider value={{ introWidgets: introWidgetMetadata, isMobile: mobileView }}>
                      <Element id={anchorId} mountLocation={mountLocation} />
                    </MetadataProvider>
                  </StyleSheetManager>
                </MobileThemeProvider>
              </ThemeProvider>
            </ThemeProvider>
          </ErrorBoundary>,
          anchorEl
        )
        metricsPublisherForManager.publishCounterMonitor(Success, 1)
      } else {
        logger.warn('cannot mount react component, anchorId not exist.', { anchorId, mobileView })
      }
    } catch (error) {
      logger.fatal(`mount react component failed, error ${error}`, { anchorId, mobileView })
      metricsPublisherForManager.publishCounterMonitor(Success, 0)
    }
  }

export const unmountComponent =
  ({ mobileView }) =>
  (event) => {
    const anchorId = event.detail.id
    const anchorEl = event.detail.anchor
    if (anchorEl) {
      // need to unmount component for SPA
      ReactDOM.unmountComponentAtNode(anchorEl)
      logger.info('unmounting react component', { anchorId, mobileView })
    }
  }

export const initIntroWidgets = (params: InitParams): void => {
  const mobileView = getIsMobileView()
  const filteredIntroWidgets = filterWidgetsWithDismissal(params.introWidgetMetadata)
  // add listeners for customer FIT components
  const mountComponentFn = mountComponent({ introWidgetMetadata: filteredIntroWidgets, mobileView: mobileView })
  const unmountComponentFn = unmountComponent({ mobileView: mobileView })
  document.addEventListener(AAC_FIT_ANCHOR_MOUNT, mountComponentFn)
  document.addEventListener(AAC_FIT_ANCHOR_UNMOUNT, unmountComponentFn)
  window.__AAC_FIT_MANAGER_READY__ = true
}

export const initNewFeatureCenter: () => void = async () => {
  if (!window.__AAC_FIT_NEW_FEATURE_LOADED__) {
    const mobileView = getIsMobileView()
    const featureLabels = getFeatureLabels()
    const fitManagerElement = document.getElementById('FITManager')
    if (fitManagerElement) ReactDOM.render(<WhatsNewCenter featureLabels={featureLabels} mobileView={mobileView} />, fitManagerElement)
    else logger.fatal('FITManager HTMLelement not found')
    window.__AAC_FIT_NEW_FEATURE_LOADED__ = true
  }
}

export const initFeedbackModal = (namespaceMetadata) => {
  if (!window.__AAC_FIT_FEEDBACK_MODAL_READY__) {
    const portalElem = document.getElementById('AACChromePortal')
    ReactDOM.render(<FeedbackModal namespaceMetadata={namespaceMetadata} />, portalElem)
    window.__AAC_FIT_FEEDBACK_MODAL_READY__ = true
  }
}
